/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */
import Model from "App/model";
import RenderObject from "App/geometry/objects/object";
import {logInfo} from "App/debug";
import ImageDataLoadersFactory from "App/utils/textures/image_data_loaders_factory";
import TexturesManager from "App/utils/textures/textures_manager";
import {DataResourceObserver} from "App/storage/data_resource_observer";
import {DataResource} from "App/storage/data_resource";
import IScene from "js_interface/src/scene_interface";

export enum SwitchingStateEnum {
    READY = 0,
    DOWNLOADING = 1,
    DOWNLOADED = 2,
    GPUPROCESSING = 3
}

/***
 * This class is responsible for receiving and preparing data to be uploaded to webgl
 * @param aSceneAPI
 * @param aTexturesManager
 * @constructor
 */
export default
class TextureDataHandler extends DataResourceObserver {

    private _UID: number;
    private _sceneAPI: IScene;
    private _texturesManager: TexturesManager;
    private _model: Model;
    private _diffuseInfo: any = {};
    private _shapesDownloadPriority: number = 100; // higher is lower priority
    private _currentLevel: number = -1;
    private _requestedLevel: number = -1;
    private _newRequestedLevel: number = -1;
    private _newRequestVisible: boolean = true; // true or false depending on whether the request is for visible or invisible texture decided by worker

    private _state: SwitchingStateEnum = SwitchingStateEnum.READY;

    private _shapes: any = {};
    private _shapeBBoxes: any = {};

    constructor(aSceneAPI, aUID, aTexturesManager, aTexture, aCreatingShape) {
        super();

        this._UID = aUID;

        this._sceneAPI = aSceneAPI;
        this._texturesManager = aTexturesManager;

        this._model = aCreatingShape.getModel();

        this._initDiffuseInfo(aTexture);
    }

    public static createTextureProperty(aUrl: string, aRelativeUrl: string, aWidth: number, aHeight: number,
                                        aOffset: number, aSize: number, aTiled: boolean, aAlpha: boolean, aDataType: string) {
        return {
            url: aUrl,
            relativeUrl: aRelativeUrl,
            width: aWidth,
            height: aHeight,
            offset: aOffset,
            size: aSize,
            tiled: aTiled,
            alpha: aAlpha,
            dataType: aDataType
        };
    }

    public getDiffuseImageData(): any {
        let diffuseImageData = null;
        let properties = null;
        for (let key in Object.keys(this._diffuseInfo)) {
            if (this._diffuseInfo[key].data) {
                diffuseImageData = this._diffuseInfo[key].data;
                properties = this._diffuseInfo[key].properties;
            }
        }

        if (!diffuseImageData || !properties) {
            return null;
        }

        // hack: only non tiled textures are supported, tiled textures will be wrongly saved
        return {
            compressedData: diffuseImageData.tiles[0].compressedUint8Data,
            relativeUrl: properties.relativeUrl
        };
    }

    public getAllDimensions(): {widths: number[], heights: number[]} {
        let dims = {
            widths: [],
            heights: []
        };

        for (let key in this._diffuseInfo) {
            let properties = this._diffuseInfo[key].properties;
            dims.widths.push(properties.width);
            dims.heights.push(properties.height);
        }

        dims.widths.sort(function(a, b) {
            return a - b;
        });

        dims.heights.sort(function(a, b) {
            return a - b;
        });

        return dims;
    }

    public getModel(): Model {
        return this._model;
    }

    /***
     * Take the bbox of the input shape and assign it to a map of shapes
     * @param aShape
     */
    public attachShape(aShape: RenderObject) {
        aShape.attachToTextureDataHandler(this);

        this._shapeBBoxes[aShape.getUID()] = {
            min: aShape.getBoundingBoxMin(),
            max: aShape.getBoundingBoxMax()
        };

        this._shapes[aShape.getUID()] = aShape;

        this._shapesDownloadPriority = Math.min(this._shapesDownloadPriority, aShape.getDownloadPriority());
    }

    public updateShapeBBox(aShape: RenderObject) {
        this._shapeBBoxes[aShape.getUID()] = {
            min: aShape.getBoundingBoxMin(),
            max: aShape.getBoundingBoxMax()
        };
    }

    public detachShape(aShape: RenderObject) {
        aShape.detachFromTextureDataHandler();

        if (this._shapeBBoxes[aShape.getUID()] !== undefined) {
            delete this._shapeBBoxes[aShape.getUID()];
        }

        if (this._shapes[aShape.getUID()] !== undefined) {
            delete this._shapes[aShape.getUID()];
        }

        this._shapesDownloadPriority = 0;

        for (let key in this._shapes) {
            this._shapesDownloadPriority = Math.max(this._shapesDownloadPriority, this._shapes[key].getDownloadPriority());
        }
    }

    public getAttachedBoundingBoxes(): any[] {
        let bboxes = [];
        for (let key in this._shapeBBoxes) {
            let bbox = this._shapeBBoxes[key];
            bboxes.push([key, bbox.min, bbox.max]);
        }

        return bboxes;
    }

    public getUID(): number {
        return this._UID;
    }

    public isEmpty(): boolean {
        return Object.keys(this._shapes).length === 0;
    }

    public decodeCrunchTexture(aTileIndex, aArrayBuffer) {
        this._texturesManager.decodeCrunchTexture(this._UID, this._requestedLevel, aTileIndex, aArrayBuffer);
    }

    public crunchTextureDecoded(aMessage: any) {
        let imageData = this._diffuseInfo[aMessage.requestedLevel].data;
        if (imageData) {
            imageData.crunchTextureDecoded(aMessage);
        }
    }

    public dataDownloaded(aResponse: any, aDataResource: DataResource) {
        this._state = SwitchingStateEnum.DOWNLOADED;

        let found = false;
        for (let key in this._diffuseInfo) {
            let properties = this._diffuseInfo[key].properties;
            if (properties.url === aDataResource.getUrl()) {
                found = true;
                if (this._diffuseInfo[key].localData) {
                    aResponse = this._diffuseInfo[key].localData;
                }

                if (!this._diffuseInfo[key].data) {
                    this._diffuseInfo[key].data = ImageDataLoadersFactory.create(properties.dataType, this, properties, +key);
                    this._diffuseInfo[key].data.parse(aResponse);
                    break;
                }
            }
        }

        // this should not happen but if so, then shit happens
        if (!found) {
            logInfo("Error: does not found url " + aDataResource.getUrl() + " in texture data handlers with uid " + this._UID);
        }
    }

    public dataDownloadFailed(aStatus, aDataResource: DataResource) {
        this._cancelResolve();
    }

    public dataDownloadAborted(aStatus, aDataResource: DataResource) {
        this._cancelResolve();
    }

    public getIsReadyToRender(): boolean {
        return this._currentLevel !== -1 && this._currentLevel === this._requestedLevel;
    }

    public requestResolution(aRequest: any) {
        // force start with level 0 at the beginning
        if (this._currentLevel === -1) {
            this._newRequestedLevel = 0;
            return;
        }

        this._newRequestedLevel = aRequest.level > this._currentLevel ? this._currentLevel + 1 : aRequest.level; // increase resolutin incrementaly
        this._newRequestVisible = aRequest.visible;
    }

    public getLoadDataQuery(): any {
        let properties = this._diffuseInfo[this._requestedLevel].properties;

        if (this._texturesManager.getTexturesResolutionLimit() < Math.max(properties.width, properties.height)) {
            return null;
        }

        return {
            url: properties.url,
            binSize: properties.size,
            priority: this._shapesDownloadPriority + this._requestedLevel,
            required: this._shapesDownloadPriority === 0 && this._requestedLevel === 0
        };
    }

    public resolve(aStatistics): boolean {
        switch (this._state) {
            case SwitchingStateEnum.READY:
                if (this._currentLevel === this._requestedLevel && this._requestedLevel === this._newRequestedLevel) {
                    return false;
                }

                if (this._currentLevel !== this._newRequestedLevel) {
                    this._requestedLevel = this._newRequestedLevel;

                    if (!this._diffuseInfo[this._requestedLevel].data) {
                        this._texturesManager.getRenderer().getDataStorage().loadData(this);
                        this._state = SwitchingStateEnum.DOWNLOADING;
                    } else {
                        // reuse already available loader for required level
                        this._state = SwitchingStateEnum.DOWNLOADED;
                    }
                }

                break;

            case SwitchingStateEnum.DOWNLOADING:
                // stop downloading of requested level when the enw requested level is at lower resolution
                if (this._requestedLevel > this._newRequestedLevel) {
                    this._texturesManager.getRenderer().getDataStorage().removeDataResources(this.getAttachedDataResources());
                    // this._cancelResolve(); // do not call cancel as it will be done via dataDownloadAborted method
                }
                break;

            case SwitchingStateEnum.DOWNLOADED:

                // stop switching when new requested level differs from new requested level
                if (this._requestedLevel !== this._newRequestedLevel) {
                    this._cancelResolve();
                    return;
                }

                this._diffuseInfo[this._requestedLevel].data.prepareData();

                if (this._diffuseInfo[this._requestedLevel].data.isReady()) {
                    this._texturesManager.getRenderer().notifySetTextureDataForShapes(this._shapes, this._diffuseInfo[this._requestedLevel].data);
                    this._state = SwitchingStateEnum.GPUPROCESSING;
                }
                break;

            case SwitchingStateEnum.GPUPROCESSING:
                if (!this._diffuseInfo[this._requestedLevel].data.hasObservers()) {
                    this._finishResolve();
                } else if (this._requestedLevel !== this._newRequestedLevel) {
                    this._cancelResolve();
                }
                break;
            default:
                break;
        }

        aStatistics._textureSwitchStats.allSwitchRequestsCount++;
        if (this._newRequestVisible) {
            aStatistics._textureSwitchStats.visibleSwitchRequestsCount++;
        }

        return true;
    }

    private _initDiffuseInfo(aTexture) {
        let texture = aTexture.get();
        for (let i = 0; i < texture.url.length; i++) {
            this._diffuseInfo[i] = {
                data: null,
                localData: i === 0 ? texture.localData : null,
                properties: TextureDataHandler.createTextureProperty(
                    texture.url[i],
                    texture.relativeUrl[i],
                    texture.width[i],
                    texture.height[i],
                    texture.offsets[i],
                    texture.sizes[i],
                    texture.tiled[i],
                    texture.alpha,
                    aTexture.dataType)
            };
        }
    }

    private _finishResolve() {
        let keepDownloadedDataInMemory = this._sceneAPI.renderSettingsModule.getStoreDownloadedTextureDataInMemory();
        if (!keepDownloadedDataInMemory) {
            this._diffuseInfo[this._requestedLevel].data.cleanupDownloadedData();
            this._diffuseInfo[this._requestedLevel].data = null;
        } else {
            this._diffuseInfo[this._requestedLevel].data.cleanupLoadedData();
        }

        this._currentLevel = this._requestedLevel;
        this._state = SwitchingStateEnum.READY;

        this._texturesManager.getRenderer().recomputeGeometryLOD();
    }

    private _cancelResolve() {
        let keepDownloadedDataInMemory = this._sceneAPI.renderSettingsModule.getStoreDownloadedTextureDataInMemory();
        if (this._diffuseInfo[this._requestedLevel].data) {
            if (!keepDownloadedDataInMemory) {
                this._diffuseInfo[this._requestedLevel].data.cleanupDownloadedData();
                this._diffuseInfo[this._requestedLevel].data = null;
            } else {
                this._diffuseInfo[this._requestedLevel].data.cleanupLoadedData();
            }
        }
        this._requestedLevel = this._currentLevel;
        this._state = SwitchingStateEnum.READY;
    }
}
